// Shared engine types. Imported by both the browser and the server — keep this file free of DOM
// and Node globals.

import type { Vec2 } from './vec2'
import type { CommandConfig, CommandType } from './commands'

export interface FenceSegment {
  a: Vec2
  b: Vec2
}

/** Axis-aligned trigger rect, in world metres. */
export interface GateRect {
  x: number
  y: number
  w: number
  h: number
}

export interface Level {
  id: string
  name: string
  /** Metric grid, metres. The canonical paddock is 70 × 120 — portrait, to fit a phone. */
  world: { w: number; h: number }
  fences: FenceSegment[]
  gate: GateRect
  dogSpawn: Vec2
  /** Sheep are scattered inside this rect by the seeded RNG. */
  flockSpawn: GateRect
  flockSize: number
  /** Set by the Big Dog bot in CI — the basis of the score formula. */
  parSeconds: number
  parBarks: number
  /**
   * 1 (gentlest) to 5 (hardest), hand-assigned per template and used to shape the week's curve.
   *
   * It does NOT change scoring: par already absorbs difficulty, so a hard Saturday has a longer par
   * rather than a harsher score, and scores stay comparable across days. See PLAN.md § M4.
   */
  difficulty: number
}

/**
 * `move` and `bark` are the original direct-control scheme, retained and disabled in the UI as a
 * hedge — see PLAN.md § Playtest feedback P3. The bot still drives through `move`, so par and the
 * solvability gate stay measured on a control scheme that has not moved.
 *
 * `pause` and `resume` are the odd ones out: **the sim ignores them entirely.**
 *
 * Replay does not need them — a paused loop simply stops producing ticks, so a log replays
 * bit-for-bit whether or not the run was ever paused. They are recorded as evidence. This is a
 * real-time game against a par time, so a player who pauses to plan every arc beats par trivially,
 * and if pause left no trace the server could not even detect it. An event the sim ignores is still
 * evidence; the cap-or-charge policy is decided later, on real data, but the logging has to exist
 * from the start or every run submitted before then is unverifiable.
 */
export type InputEventType = 'move' | 'bark' | 'pause' | 'resume' | CommandType

/**
 * An input, quantised to a sim tick. Pointer events arrive at arbitrary rates; they are recorded
 * against tick boundaries so the log replays identically regardless of the device's event rate.
 */
export interface InputEvent {
  tick: number
  type: InputEventType
  x: number
  y: number
}

/** The full record needed to reproduce a run. Submitted to the server for verification in phase 4. */
export interface RunLog {
  levelId: string
  seed: number
  dailyKey: string
  events: InputEvent[]
  ticks: number
}

/**
 * Extends `CommandConfig` rather than restating it, so the handler-command constants are documented
 * once, next to the code that reads them.
 */
export interface SimConfig extends CommandConfig {
  // --- flocking ---
  perceptionRadius: number
  separationRadius: number
  cohesionWeight: number
  alignmentWeight: number
  separationWeight: number

  // --- contentment ---
  //
  // Without these, every flocking rule emits a full `maxForce` however small the error is, so the
  // net force never vanishes and the flock circles forever. See PLAN.md § Open problem 1.
  //
  /**
   * Cohesion dead zone, as a multiple of the flock's natural packing radius
   * (`separationRadius × sqrt(flockSize / 3)`). Inside it, cohesion produces exactly zero force.
   * Derived per-level rather than fixed, because the radius a flock settles into scales with size.
   */
  contentmentFactor: number
  /** Metres over which cohesion ramps 0 → full beyond the dead zone. Avoids a force discontinuity. */
  cohesionRamp: number
  /** Alignment dead zone: a velocity mismatch below this (m/s) produces zero force. */
  alignTolerance: number
  /** m/s over which alignment ramps 0 → full beyond its dead zone. */
  alignRamp: number
  maxSpeed: number
  maxForce: number
  /**
   * Per-second velocity damping at zero stress, tapering to zero at full stress — "grazing".
   *
   * Textbook boids have no damping: agents hold whatever speed the forces give them and circle
   * forever. That makes the settle-based win condition unreachable, because a penned flock never
   * slows down. Scaling the damping by calmness also ties settling to the stress system, so a
   * panicked flock genuinely cannot be penned until it calms — which is the whole game.
   */
  grazeDamping: number

  // --- dog ---
  dogSpeed: number
  dogRadius: number
  dogForce: number
  /**
   * Ticks the dog spends gathering itself at an interior fence before it goes over.
   *
   * The dog still jumps fences — they constrain the flock, not the player (PLAN.md § The dog is
   * bounded by the paddock) — but gliding through as if they were not there read as a bug. A jump
   * that costs time makes a fence a real cost without making it a wall, and needs no pathfinding,
   * which matters because commands auto-navigate the dog and the bot has no route planner.
   */
  /**
   *
   * How close the dog will pass an individual sheep before steering around it, in metres.
   *
   * Per sheep rather than around the mob, because the dog *works at the mob's edge*: a keep-out disc
   * sized to the flock contains the dog nearly all the time, and the first attempt at this never
   * engaged once. The rule is not "stay away from the flock", it is "do not run through the animals".
   */
  /** How much astern pressure becomes forward motion along the animal's own line. */
  balanceBias: number
  /** How much the flight zone varies with temperament, as a fraction of `dogRadius`. */
  flightZoneSpread: number
  sheepClearance: number
  /** Ticks of travel to look ahead when deciding to go round a sheep, on top of the clearance. */
  avoidLookTicks: number
  dogJumpTicks: number
  barkRadiusMultiplier: number
  barkForceMultiplier: number
  barkDurationTicks: number
  barkCooldownTicks: number

  // --- grazing / wander ---
  //
  // Left alone, sheep alternate between head-down grazing and a few idle steps. This is mostly
  // character, but it is deliberately gentle: wander's terminal speed is `wanderForce/grazeDamping`,
  // which MUST stay below `settleSpeed` or the flock never counts as settled and the puzzle becomes
  // unwinnable. Check that ratio before raising either constant.
  wanderForce: number
  /** How fast the wander direction drifts. Higher = more skittish, less purposeful. */
  wanderJitter: number
  /** Ticks spent head-down before taking a few steps, randomised per sheep in this range. */
  grazeTicksMin: number
  grazeTicksMax: number
  /** Ticks spent wandering before settling back to graze. */
  wanderTicksMin: number
  wanderTicksMax: number

  // --- individual temperament ---
  //
  // Every sheep draws a `boldness` and a `sociability` in −1 … +1 at spawn; these say how much
  // those draws matter. 0 for either makes the flock uniform again, which is the control condition
  // when tuning.
  //
  // Deliberately NOT applied to any perception radius. `SpatialGrid` sizes its cells from the
  // largest radius any entity can perceive at, and a per-sheep radius would silently make some
  // queries wider than the cell — which misses neighbours rather than erroring.
  /** Fraction by which fright varies between the boldest and most timid sheep. */
  traitBoldness: number
  /** Fraction by which cohesion varies between the most herd-bound and most independent. */
  traitSociability: number

  // TICK-DENOMINATED CONSTANTS AND SIM_SPEED — the trap that bit when the world was slowed.
  //
  // `SIM_SPEED` is documented as preserving every tuned ratio by construction, and for anything in
  // m/s or m/s² that is true. It is NOT true of anything counted in TICKS. A tick is a fixed slice
  // of REAL time, so `DT` world-seconds per tick scales with SIM_SPEED and a duration written in
  // ticks silently changes what it means in the world.
  //
  // Lowering SIM_SPEED 3.0 → 2.0 therefore shortened every one of these by a third without touching
  // them, and three tests caught it: flight run-on stopped carrying a frightened sheep at all, the
  // lift stopped arming because the dog covered less arc per tick, and the bot's cast timed out
  // mid-cast and reversed direction. The world-duration ones below were multiplied by 1.5 to undo
  // that. Ones that are genuinely REAL-time — the bark cooldown a player waits out, the pause at a
  // fence — were deliberately left alone.
  //
  // Anything added here in ticks must be classified the same way before SIM_SPEED moves again.

  // --- stress ---
  /** Per-second stress gained at the centre of the dog's radius, falling off to 0 at the edge. */
  stressGainRate: number
  /** Per-second decay when unpressured. Without this the flock latches at 1.0 and never recovers. */
  stressDecayRate: number
  /**
   * Fright from a dog *bearing down*, per (m/s of closing speed × second), at the dog's centre.
   *
   * The counterpart to `stressGainRate`, which is a rate and therefore blind to speed: a dog
   * crossing the mob covers a metre in a tick or so, gains a sheep about 0.08 stress on the way
   * past, and the flock does not react at all. Scaled by closing speed so it is exactly zero for a
   * dog holding station, lying down, or moving away, and quadratic in proximity because fright is
   * not linear in distance.
   */
  stressChargeGain: number
  /** Distance at which a fast dog counts as having bitten rather than pushed, in metres. */
  biteRadius: number
  /** Fraction of `dogSpeed` above which a close pass is a bite at all. */
  biteSpeedFraction: number
  stressBarkSpike: number
  /**
   * Neighbour contagion gain per second. MUST stay below 1.0 — at or above it the whole flock
   * saturates permanently and the puzzle becomes unwinnable.
   */
  stressContagion: number
  /** Hysteresis band: enter panic above `panicEnter`, leave only below `panicExit`. */
  panicEnter: number
  panicExit: number
  panicSpeedMultiplier: number
  /** Cohesion at full working pressure, before panic. Sheep bunch under threat. */
  bunchCohesionMultiplier: number
  /** Personal space at full working pressure — a pressed mob tolerates closer contact. */
  bunchSeparationMultiplier: number
  /** How far the contentment radius tightens under full working pressure. */
  bunchContentDrop: number
  panicSeparationMultiplier: number
  panicCohesionMultiplier: number
  /**
   * How long a frightened sheep keeps running after the fright itself has faded, in ticks, at full
   * stress. Scaled by the stress that triggered it, so a hard push runs on and a mild one does not.
   *
   * Without this, run-on is governed entirely by `stressDecayRate`: the instant stress crosses back
   * under `panicExit` the sheep damps hard and stops almost on the spot, which reads as the flock
   * having no momentum of its own.
   */
  flightTicksMax: number
  /**
   * Random jitter force on a frightened sheep, at full stress.
   *
   * The counterpart to `wanderForce`, and deliberately separate from it: wander is the graze cycle
   * and fades OUT as stress rises (a sheep with its head up is not grazing), which left panicking
   * sheep gliding on pure flocking forces — the most predictable things in the paddock. Panic
   * should be the least predictable. This fades IN with stress instead.
   */
  /**
   * Outward force on a sheep in flight, at full flight, in the direction of its worst fright.
   *
   * Damping suppression alone makes a frightened sheep COAST, and coasting is not fleeing — a dog
   * charging through a settled mob left it at 1.27 m/s against a panicked cap of 16. Dog repulsion
   * is the only other outward force and it vanishes beyond `dogRadius`, so nothing survived the
   * two ticks of contact. Keep this below `maxForce`: a single term above the acceleration clamp
   * silently zeroes every flocking rule while it is active.
   */
  fleeForce: number
  panicJitterForce: number
  /** Stress below which panic jitter is silent, so calm sheep are unaffected. */
  panicJitterThreshold: number
  /**
   * How far a frightened sheep's flocking perception reaches, as a multiple of `perceptionRadius`
   * at full stress. A panicking animal is aware of far more of the mob than a grazing one.
   *
   * MIND THE GRID. `SpatialGrid` sweeps a fixed 3×3 cell window and the caller filters by distance,
   * so cell size IS the search window: a query wider than the cell silently MISSES neighbours
   * rather than erroring. The grid is therefore sized from `perceptionRadius × this`, not from
   * `perceptionRadius`. Raising this widens every cell and costs neighbour-scan time quadratically.
   */
  panicPerceptionMultiplier: number
  /**
   * The same, for stress contagion.
   *
   * Split from `panicPerceptionMultiplier` deliberately. Contagion and flocking share
   * `perceptionRadius`, so one knob used to drive two quite different effects — how much of the mob
   * a sheep moves with, and how fast fear spreads through it. They want tuning apart.
   */
  panicContagionMultiplier: number

  // --- fences ---
  fenceAvoidRadius: number
  fenceAvoidWeight: number
  /** Sheep body radius for collision resolution. */
  /** How fast a sheep can change the way it points, in radians per WORLD second. */
  turnRate: number
  /** Fraction of speed that survives moving at right angles to the way it is pointing. */
  turnSpeedFloor: number
  /** Below this speed the animal is shuffling rather than travelling, and may move sideways. */
  strafeBelowSpeed: number
  sheepRadius: number

  // --- win condition ---
  /** Mean flock speed below this counts as settled. */
  settleSpeed: number
  settleTicks: number
}

export const DEFAULT_CONFIG: SimConfig = {
  perceptionRadius: 6,
  separationRadius: 1.6,
  cohesionWeight: 1.0,
  alignmentWeight: 0.8,
  separationWeight: 1.6,

  contentmentFactor: 1.0,
  cohesionRamp: 1.5,
  alignTolerance: 0.25,
  alignRamp: 0.75,
  /**
   * Sheep speed CAP, not their usual pace. Contentment means sheep only approach it under real
   * pressure, so a high cap makes fleeing dramatic without making grazing frantic — idle sheep
   * still drift at ~0.25 m/s.
   *
   * Held just under the dog: panicked sheep reach `maxSpeed × panicSpeedMultiplier` = 16 against a
   * dogSpeed of 18. The dog must stay the faster animal or a spooked flock can never be recovered
   * and the puzzle becomes unwinnable.
   */
  maxSpeed: 6.5,
  maxForce: 6.0,
  grazeDamping: 1.2,

  // A working kelpie is the fastest thing in the paddock, and must stay above panicked sheep
  // (maxSpeed × panicSpeedMultiplier = 16) or a spooked flock outruns the dog permanently and the
  // puzzle becomes unrecoverable. Keep this invariant if either constant moves — there is a test.
  dogSpeed: 14.0,
  dogRadius: 9.0,
  // Must stay comparable to maxForce, not far above it. Total acceleration is clamped to maxForce,
  // so an oversized dogForce (26 vs 6) saturates the clamp on its own and scales cohesion away to
  // nothing — the flock stops behaving like a flock the instant the dog is in range and simply
  // explodes. At 10, dog pressure competes with cohesion instead of erasing it.
  dogForce: 10.0,
  // Swept against the per-template gate: 4 ticks holds 160/160 across dogleg, chicane, split-race
  // and island; 8 already costs two seeds and 20 costs three. The bot jumps fences constantly — it
  // drives in straight lines — so it pays this over and over, and the ceiling is the BOT's, not the
  // game's. Re-sweep when the bot speaks commands: a dog that casts around a mob crosses far fewer
  // fences, and this can probably be a real cost rather than the hitch it is now.
  // Swept for BOTH properties at once, because they trade off: how close a cast passes a sheep, and
  // what the solvability gate costs. 2.6 m with 3 ticks of look-ahead is the corner — 140/140 with a
  // 3.5 m closest pass, against 1.9 m before any of this and 140/140. Wider is not free: 3.0 m with
  // 6 ticks passes at 4.0 m but drops the gate to 137/140.
  /**
   * How much pressure from BEHIND the point of balance becomes forward motion along the animal's
   * own line, rather than motion directly away from the dog.
   *
   * Stock handling's oldest idea: an animal has a point of balance at its shoulder, and pressure
   * behind it drives the animal ON while pressure ahead of it turns or checks the animal. The
   * simulation had no notion of it — repulsion was purely radial, so the dog's distance mattered and
   * its ANGLE did not, and a mob driven from astern fanned outward instead of travelling as a body.
   *
   * The turning half needs nothing new: since sheep gained a facing they cannot strafe, so a push at
   * the shoulder already rotates the animal rather than sliding it sideways.
   */
  balanceBias: 0.6,
  /**
   * How much a sheep's flight zone varies with its temperament, as a fraction of `dogRadius`.
   *
   * A bold animal lets the dog come closer before it moves; a timid one goes early. Reusing the
   * `boldness` trait rather than drawing a new number keeps the RNG stream — and therefore every
   * daily ever generated — exactly where it is.
   */
  flightZoneSpread: 0.25,
  sheepClearance: 2.6,
  avoidLookTicks: 3,
  dogJumpTicks: 4,
  barkRadiusMultiplier: 3.0,
  barkForceMultiplier: 3.0,
  barkDurationTicks: 34,
  barkCooldownTicks: 45,

  // --- handler commands (see commands.ts for what each one means) ---
  //
  // These are starting values, not measured ones. Nobody has played a commanded run, and the bot
  // still drives through `move`, so there is no clock to fit them against yet — expect the first
  // human session to move all five. `scripts/trace.ts` is how to read them, not guesswork.
  // All measured from the mob's EDGE, so a station means the same thing to a tight mob as to a
  // strung-out one. See CommandConfig for why that is a fix rather than a preference.
  flankRadiusFactor: 0.6,
  flankEscapeTangent: 0.3,
  // 0.75 rather than 0.5: the LOWEST level of "walk on" should get the mob moving at a slow steady
  // walk, not shove it. Repeats bring the dog in from there, so the old strength is still reachable
  // at full press — it is just no longer where the command starts.
  walkOnRadiusFactor: 0.75,
  // 0.2 rather than 0.15 because the pace scale is 1-based: pace 1 is the base walk and leans by
  // nothing, so the three steps above it have to cover the same ground four used to. Max lean is
  // unchanged at 0.6 of dogRadius.
  walkOnPressStep: 0.2,
  walkOnPressMax: 4,
  /**
   * How much faster the dog works per notch of walk-on pace, as a fraction of `dogSpeed`.
   *
   * Playtest: "when the dog is commanded to walk on it should move a little faster, just slightly on
   * the first go." Slightly is the whole specification — 5% at pace 1, and 20% at a full press,
   * which is the difference between a dog ambling behind a mob and one meaning it. It multiplies
   * with `steadyFactor` rather than replacing it, so a steady walk-on is still slower than a plain
   * one at the same pace.
   */
  walkOnPaceStep: 0.05,
  /**
   * The most a drive will swing its push line toward the gate, as a cosine/sine pair — 30°.
   *
   * Playtest: "when the dog walks on it should generally aim to approach the goal." It used to push
   * along whatever line the dog happened to be standing on, so a drive from slightly off the balance
   * point sent the mob slightly off the gate and stayed that way.
   *
   * **Bounded, and measured against a FIXED anchor rather than the dog's live position.** The first
   * version swung a fraction of the error each tick, which converges: the station walks round to
   * balance, the dog follows it, and "walk on" issued once quietly performs the outrun and drives
   * home. Measured on PLAN.md § P8's own bench, that took walk-on alone from **1/42 to 25/42** —
   * the exact fault P8 exists to prevent, arrived at from a different direction. The swing is now
   * taken from where the dog stood when the order was given, so it is capped by construction and no
   * amount of time turns a badly placed dog into a well placed one. That is still the flank's job.
   *
   * Stored as a cosine and a sine because the sim path may not call `Math.cos`; see commands.ts.
   */
  walkOnLeadCos: 0.866,
  walkOnLeadSin: 0.5,
  walkOnFenceRadius: 6.0,
  /**
   * How squarely a push must already be heading at the gate for the fence rule to stand aside.
   *
   * **The gate is a GAP IN THE PERIMETER FENCE.** Without this, the last few metres of every run are
   * a drive at a wall as far as the fence rule can tell, and it dutifully strips out the one
   * component that puts the mob through the gap — so the dog shepherds them along the rail past the
   * opening instead of into it. Measured on the per-template gate, the unexempted rule cost **7 of
   * 280 seeds** and pushed p90 from 80.6 s to 141.3 s. 0.6 is about 53°.
   */
  walkOnFenceSkipCos: 0.6,
  /**
   * How much further out each "get back" puts the dog, as a fraction of `dogRadius`.
   *
   * 0.25 rather than 0.45, and this is the first time it has been measured: the offset was only ever
   * applied to the flank, so the command did nothing and the value described nothing. At 0.45 the
   * FIRST press already put the dog outside the 9 m pressure radius, which left three notches that
   * all meant the same thing. At 0.25 a full press is one whole radius further out and each step
   * changes what the flock feels.
   */
  getBackStep: 0.25,
  getBackMax: 4,
  // How many walk-on presses a "steady" winds back. Two of four, so twice steady takes a full press
  // down to nothing — a moderation rather than a stop, which is what separates it from giving a new
  // command. Pace 0 is only reachable this way: it is the dog still driving, but eased right off.
  steadyPressRelief: 2,
  steadyFactor: 0.45,
  // 1.0 puts the nearest sheep exactly on the falloff edge, where the dog's force is zero. That is
  // what makes "get behind" a position rather than a shove — and it is the whole fix for the command
  // solving 34/42 levels unaided.
  holdRadiusFactor: 1.0,
  recallRadiusFactor: 1.5,
  flankRadialGain: 0.6,
  // A lying dog is still there, so this is a release of pressure rather than an absence of it.
  // Zero would let the flock treat a dog at two metres as empty ground, which is not the command.
  dropPressure: 0.25,

  // Terminal wander speed = wanderForce / grazeDamping = 0.30 / 1.2 = 0.25 m/s. Visible as drifting
  // about, and still comfortably under settleSpeed (0.45) so a grazing flock registers as settled —
  // with only ~a third of the flock stepping at any moment, the mean lands around 0.1 m/s.
  wanderForce: 0.3,
  wanderJitter: 0.35,
  grazeTicksMin: 169,
  grazeTicksMax: 563,
  wanderTicksMin: 56,
  wanderTicksMax: 225,

  // Swept against the 30-day gate. 0.2/0.2 gives a better tail than a UNIFORM flock (worst 77.8 s
  // against 116.0 s), which is the same effect panic jitter had in M4½: variation breaks the
  // symmetric deadlocks that identical agents fall into. At 0.5/0.6 the gate drops to 29/30.
  traitBoldness: 0.2,
  traitSociability: 0.2,
  stressGainRate: 0.9,
  // Slower decay, same reason: fright should outlast the thing that caused it by more than a beat.
  stressDecayRate: 0.26,
  stressChargeGain: 0.25,
  biteRadius: 1.1,
  biteSpeedFraction: 0.45,
  stressBarkSpike: 0.45,
  stressContagion: 0.4,
  panicEnter: 0.7,
  panicExit: 0.4,
  panicSpeedMultiplier: 1.8,
  /**
   * What pressure does to a flock BEFORE it panics: it bunches.
   *
   * The model used to have only the panic half — separation ×4 and cohesion ×0.25 — so pressure of
   * any kind made sheep spread out and stop holding together. That is backwards for the band the dog
   * actually works in, and it is backwards about the animal: sheep bunch under threat, which is the
   * entire reason herding works at all. A dog that could only ever scatter its stock is not a
   * sheepdog.
   *
   * These three are the fully-pressed end of the working band (stress → `panicEnter`), interpolated
   * from 1.0 at rest. Past `panicEnter` they hand over to the panic values below, so the flock balls
   * up as the dog leans on it and blows apart only when it is genuinely frightened — which is the
   * signal the player is supposed to be reading.
   */
  bunchCohesionMultiplier: 2.0,
  bunchSeparationMultiplier: 0.8,
  /** How far the contentment radius tightens under full working pressure — the resting spread. */
  bunchContentDrop: 0.45,
  panicSeparationMultiplier: 4.0,
  panicCohesionMultiplier: 0.25,
  // 60 ticks = 1 real second = 3 world-seconds of coasting at full fright. Long enough that a
  // flock pushed hard carries on past where the dog stopped pushing — which is the whole point of
  // patient arcs — and short enough that it does not stop the flock ever settling into the gate.
  // 45 ticks = 0.75 real seconds = 2.25 world-seconds of coasting at full fright.
  //
  // Swept 60/45/30/20 against the gate. The sensitivity is NOT monotonic — 60 lost a daily seed and
  // a split-race seed, 45 and 20 held everything, 30 lost a split-race seed — so this is a marginal
  // seed tipping on chaos, not a clean threshold. 45 is the longest run-on measured clean; if the
  // M4 template expansion makes this recur, the fix is bot robustness (it cannot recover a stray
  // that coasts away from the mob), not a smaller number here.
  // Raised on playtest — "flock should stay panicked for a bit longer". A frightened mob that
  // recovers in a second reads as unbothered. Still under the 2-second ceiling the settle test pins,
  // because run-on suppresses damping and a flight that outlives the settle window would stop a
  // penned flock ever registering a win.
  flightTicksMax: 125,
  // Held well under maxForce (6.0) — jitter should scuff the flight path, not steer it. Above ~1.5
  // it starts to overpower dog repulsion and sheep jiggle instead of fleeing.
  // 2.5 is the strongest value the solvability gate carries: at 3.5 the bot loses a dogleg seed and
  // its worst case blows out to 140 s. Swept against the gate, not chosen. See PLAN.md § P10.
  fleeForce: 2.5,
  panicJitterForce: 0.9,
  // panicExit, so jitter is present through the whole hysteresis band rather than switching off
  // the moment a sheep technically stops being "panicked".
  panicJitterThreshold: 0.4,
  // 6m -> 10.8m at full fright. Grid cells grow to 10.8m to match, which is a real cost: each 3×3
  // sweep covers 3.24x the area, so neighbour scans do ~3x the work at the same flock size. Fine at
  // 10-18 sheep; re-measure before the 200-sheep performance target.
  panicPerceptionMultiplier: 1.8,
  // Fear spreads a little further than flocking attention does — a sheep does not need to be moving
  // with you to catch your fright.
  panicContagionMultiplier: 2.0,

  fenceAvoidRadius: 3.0,
  fenceAvoidWeight: 2.5,
  /**
   * How fast a sheep can change the way it is pointing, in radians per WORLD second.
   *
   * Playtest asked for realistic sheep behaviour, and the least realistic thing about them was that
   * they had no facing at all: velocity could swing to any direction the forces asked for, so a mob
   * under pressure slid sideways like filings under a magnet. Animals turn, and turning takes time.
   *
   * 6 rad/s is a half-turn in about half a world second. It is a WORLD rate, so it survives
   * SIM_SPEED moving — unlike anything written per tick (§ Open problem 6).
   *
   * **Swept against the gate, not chosen.** At 4 the last few head of `island` #25 could be jammed
   * between the block and the dog with no room to turn out: the bot held maximum press with the dog
   * 1.3 m from the mob, barked 246 times and never moved them, and the seed did not solve in 900 s.
   * 6 and 8 both clear 280/280; 6 is taken for its tail (worst seed 243 s against 322 s), which is
   * the fairness number this project keeps having to defend.
   */
  turnRate: 6.0,
  /**
   * How much speed survives running at right angles to where the sheep is pointing.
   *
   * Movement follows the nose, so a sheep that wants to go somewhere it is not facing has to slow,
   * turn, and set off — which is what makes a flock take a beat to break in a new direction instead
   * of teleporting its velocity. Not zero: a real animal still carries some way through a turn, and
   * zero here would let a mob be stopped dead by asking it to change its mind.
   */
  turnSpeedFloor: 0.5,
  /**
   * Below this speed a sheep may still move sideways, in world m/s.
   *
   * A running animal cannot strafe; a standing one shuffles, and sheep in a crowded mob do exactly
   * that all the time. It is also what keeps the no-overlap pass working: with movement locked to
   * the nose at every speed, two jammed sheep could no longer step apart and the worst separation
   * went 0.792 m → 0.703. Walking pace, so anything actually travelling is committed to its line.
   */
  strafeBelowSpeed: 1.0,
  sheepRadius: 0.4,

  settleSpeed: 0.45,
  settleTicks: 56,
}

/**
 * Faults, counted in the engine as they happen.
 *
 * **Counted here rather than in the client, and that is a correctness requirement rather than
 * tidiness.** The server replays the input log and must derive an identical scorecard (§ M7), so a
 * fault the browser tallies is a fault a forged run can invent. These ride along in `RunResult`
 * beside `meanStress` for exactly that reason.
 */
export interface Faults {
  /** Times the flock was frightened into panic, having been calm. The stress penalty made countable. */
  rushes: number
  /** Times the dog came close enough, fast enough, to be a bite rather than a push. */
  bites: number
  /** Sheep that got into the gate and came back out. */
  penEscapes: number
  /** Times the dog put itself between the mob and the gate — the destination-relative Cross. */
  crosses: number
}

export interface RunResult {
  won: boolean
  ticks: number
  elapsedMs: number
  barks: number
  /** Mean flock stress integrated over the run, normalised to [0,1]. */
  meanStress: number
  penned: number
  flockSize: number
  faults: Faults
  stateHash: string
}
